import { cn } from '@/lib/utils';
import { ServiceRoleChip, ServiceRoleChipSkeleton } from './product-label';

/**
 * Inline "short-code chip + role name" pair — the canonical way to render a
 * service role anywhere it appears as a value rather than as a form control:
 * the budget sheet's per-role rows, the role picker's dropdown items, and any
 * future list, table cell, or detail row.
 *
 * Sibling of {@link ProductLabel}, which does the same job for a product, and
 * built on the same {@link ServiceRoleChip}. Use this rather than hand-rolling
 * `chip + name` or a `"CODE — Name"` string: those drift apart on spacing,
 * truncation, and what happens when the code is missing, and a role rendered
 * two different ways on two screens reads as two different things.
 *
 * For a plain STRING (a `<select>` option, an aria-label, an export column)
 * there is nothing to compose — use the role's `name`, or `formatRoleLabel`
 * where the code has to ride along in text.
 *
 * The three `code` states are deliberately distinct, because collapsing them
 * lies to the reader (G15):
 *
 *   string    — resolved; renders the chip.
 *   null      — resolved, but the role has no code (or is unknown); renders
 *               the name alone rather than an empty chip.
 *   undefined — still loading; renders a skeleton chip so the row doesn't
 *               shift when the code lands a few frames after the name.
 */
export function ServiceRoleLabel({
  code,
  name,
  className,
  truncate = true,
}: {
  /** The role's short code (`serviceRole.shortCode`). See the state
   *  semantics above — `undefined` means loading, `null` means none. */
  code?: string | null;
  /** The role's display name (`serviceRole.name`). */
  name: string;
  className?: string;
  /** Truncate the name with ellipsis at the container width. On by default
   *  so the label drops into tight rows without breaking layout; pass
   *  `false` for headings or anywhere the parent already controls width. */
  truncate?: boolean;
}) {
  return (
    // Layout utilities applied LAST so a caller's className can't swap out
    // the display mode and break the name span's truncation, which depends
    // on being a flex item with min-width: 0. tailwind-merge keeps the later
    // classes winning on conflict. Same rule as ProductLabel.
    <span className={cn(className, 'inline-flex min-w-0 items-center gap-1.5')}>
      {code === undefined ? (
        <ServiceRoleChipSkeleton />
      ) : code ? (
        <ServiceRoleChip code={code} title={`Service role: ${code}`} />
      ) : null}
      {/* `block min-w-0` forces truncation to work inside flex/grid parents
       *  that don't establish a min-width-0 chain — `overflow: hidden` is a
       *  no-op on inline elements. */}
      <span className={cn('block min-w-0', truncate && 'truncate')}>{name}</span>
    </span>
  );
}

/** Minimum role shape {@link ServiceRoleList} needs to render a row. A
 *  structural subset of `@clockwise/clients` `ServiceRole`, so callers pass
 *  their full role objects directly without a mapping step. */
export interface ServiceRoleListItem {
  id: string;
  /** Short code, or `null`/empty when the role has none — rendered as the
   *  name alone (see {@link ServiceRoleLabel}'s `code` semantics). */
  shortCode?: string | null;
  name: string;
}

/**
 * Comma-separated run of {@link ServiceRoleLabel}s — the canonical way to
 * render "this activity's roles" wherever more than one role appears in a
 * single value: the activity list's "Additional access" column, the activity
 * detail page's "Also visible to" row, and any future multi-role cell.
 *
 * Each item is itself a chip + name pair, so a bare gap between them leaves
 * "CTR Controller CFO CFO" ambiguous about where one role ends and the next
 * begins. The comma rides INSIDE each item's wrapper (not as a separate flex
 * child) so it can never wrap onto a line by itself, and the last item has
 * none. Callers own the empty state — the copy differs per screen ("—",
 * "— only the primary role") — so this renders nothing for an empty list.
 */
export function ServiceRoleList({
  roles,
  className,
  truncate = false,
}: {
  roles: readonly ServiceRoleListItem[];
  className?: string;
  /** Passed through to each {@link ServiceRoleLabel}. Off by default: a role
   *  list sits in narrow table cells where a name clipped to "Acc…" is worse
   *  than one that wraps, and the row is already sized by other columns. */
  truncate?: boolean;
}) {
  return (
    <span className={cn('flex flex-wrap items-center gap-x-1.5 gap-y-1', className)}>
      {roles.map((r, i) => (
        <span key={r.id} className="inline-flex items-center">
          <ServiceRoleLabel code={r.shortCode || null} name={r.name} truncate={truncate} />
          {i < roles.length - 1 ? ',' : null}
        </span>
      ))}
    </span>
  );
}
